import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import StatusPipeline from '../components/StatusPipeline';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Download, Star, Loader2, CheckCircle, User,
  FileDown, RotateCcw, Check
} from 'lucide-react';

export default function DocumentDetailPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [rating, setRating] = useState([5]);
  const [ratingComment, setRatingComment] = useState('');
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [downloading, setDownloading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDoc();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDoc = async () => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
      if (data.storage_path) loadImage(data.storage_path);
    } catch {
      toast.error('Document not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadImage = async (path) => {
    try {
      const response = await api.get(`/files/${path}`, { responseType: 'blob' });
      setImageUrl(URL.createObjectURL(response.data));
    } catch { /* silent */ }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await api.post(`/documents/${id}/claim`);
      toast.success('Claimed! Opening workspace...');
      navigate(`/workspace/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.post(`/documents/${id}/approve-transcription`);
      toast.success('Transcription approved!');
      fetchDoc();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const handleRate = async () => {
    try {
      await api.post(`/documents/${id}/rate`, { rating: rating[0], comment: ratingComment });
      toast.success('Rating submitted!');
      setRatingOpen(false);
      fetchDoc();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleRevision = async () => {
    if (!revisionNotes.trim()) { toast.error('Please describe what needs revision'); return; }
    try {
      await api.post(`/documents/${id}/revision`, { notes: revisionNotes });
      toast.success('Revision requested');
      setRevisionOpen(false);
      fetchDoc();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const response = await api.get(`/documents/${id}/download?format=${format}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.title}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }
  if (!doc) return null;

  const isUploader = user && doc.uploader_id === user._id;
  const isTranscriber = user && doc.transcriber_id === user._id;
  const canClaim = user && (user.role === 'transcriber' || user.role === 'admin') && doc.status === 'pending' && !isUploader;
  const canApprove = isUploader && doc.status === 'review';
  const canRate = isUploader && (doc.status === 'done' || doc.status === 'review') && !doc.rating;
  const canDownload = doc.status === 'done' || doc.status === 'review';
  const canRevise = isUploader && (doc.status === 'review' || doc.status === 'done');
  const canWork = isTranscriber && (doc.status === 'assigned' || doc.status === 'typing');

  return (
    <div className="pb-nav" data-testid="document-detail-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-[var(--brand-text-sec)] hover:text-[var(--brand-text)] mb-4 transition-colors"
        >
          <ArrowLeft size={18} /> Back
        </button>

        {/* Document header */}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-1" data-testid="doc-title">
          {doc.title}
        </h1>
        {doc.description && <p className="text-sm text-[var(--brand-text-sec)] mb-4">{doc.description}</p>}

        {/* Status Pipeline */}
        <div className="bg-white border border-[var(--brand-border)] p-6 mb-4">
          <StatusPipeline currentStatus={doc.status} />
        </div>

        {/* Meta info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {doc.selected_price != null && doc.selected_price > 0 && (
            <div className="bg-white border border-[var(--brand-border)] p-3 text-center">
              <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Price</p>
              <p className="text-lg font-black font-mono text-[var(--brand-primary)]" data-testid="doc-price">
                ${doc.selected_price.toFixed(2)}
              </p>
            </div>
          )}
          {doc.offered_price != null && doc.offered_price > 0 && !doc.selected_price && (
            <div className="bg-white border border-[var(--brand-border)] p-3 text-center">
              <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Price</p>
              <p className="text-lg font-black font-mono text-[var(--brand-primary)]" data-testid="doc-price">
                ${doc.offered_price.toFixed(2)}
              </p>
            </div>
          )}
          <div className="bg-white border border-[var(--brand-border)] p-3 text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Pages</p>
            <p className="text-lg font-bold font-mono text-[var(--brand-text)]">{doc.page_count || 1}</p>
          </div>
          <div className="bg-white border border-[var(--brand-border)] p-3 text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Words</p>
            <p className="text-lg font-bold font-mono text-[var(--brand-text)]">~{doc.word_count || 0}</p>
          </div>
          <div className="bg-white border border-[var(--brand-border)] p-3 text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Difficulty</p>
            <Badge className={`rounded-sm text-[10px] ${
              doc.difficulty === 'easy' ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]' :
              doc.difficulty === 'medium' ? 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)]' :
              'bg-[var(--brand-danger)]/10 text-[var(--brand-danger)]'
            }`}>
              {(doc.difficulty || 'medium').charAt(0).toUpperCase() + (doc.difficulty || 'medium').slice(1)}
            </Badge>
          </div>
        </div>

        {/* Document image */}
        {imageUrl && (
          <div className="bg-white border border-[var(--brand-border)] mb-4" data-testid="doc-image">
            <img src={imageUrl} alt={doc.title} className="w-full max-h-[400px] object-contain p-4" />
          </div>
        )}

        {/* Transcriber info */}
        {doc.transcriber_name && (
          <div className="bg-white border border-[var(--brand-border)] p-4 mb-4 flex items-center gap-3">
            <User size={18} className="text-[var(--brand-text-sec)]" />
            <span className="text-sm text-[var(--brand-text-sec)]">
              Transcriber: <span className="font-semibold text-[var(--brand-text)]">{doc.transcriber_name}</span>
            </span>
          </div>
        )}

        {/* Transcribed text */}
        {doc.transcribed_text && (
          <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="transcribed-text-section">
            <h3 className="text-sm font-bold tracking-tight text-[var(--brand-text)] mb-2 flex items-center gap-2">
              <CheckCircle size={16} className="text-[var(--brand-accent)]" /> Transcribed Text
            </h3>
            <p className="text-sm text-[var(--brand-text)] leading-relaxed whitespace-pre-wrap font-mono">
              {doc.transcribed_text}
            </p>
          </div>
        )}

        {/* Download Section */}
        {canDownload && doc.transcribed_text && (
          <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="delivery-section">
            <h3 className="text-sm font-bold tracking-tight text-[var(--brand-text)] mb-3 flex items-center gap-2">
              <FileDown size={16} /> Download
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {['pdf', 'docx', 'txt'].map(fmt => (
                <button
                  key={fmt}
                  data-testid={`download-${fmt}`}
                  onClick={() => handleDownload(fmt)}
                  disabled={downloading === fmt}
                  className="bg-[var(--brand-bg)] border border-[var(--brand-border)] p-3 text-center hover:bg-[var(--brand-primary)] hover:text-white hover:border-[var(--brand-primary)] transition-all font-medium text-sm disabled:opacity-50"
                >
                  {downloading === fmt ? (
                    <Loader2 size={14} className="animate-spin mx-auto" />
                  ) : (
                    fmt.toUpperCase()
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rating display */}
        {doc.rating && (
          <div className="bg-white border border-[var(--brand-border)] p-4 mb-4 flex items-center gap-3" data-testid="rating-display">
            <Star size={18} className="text-[var(--brand-warning)]" />
            <span className="text-sm text-[var(--brand-text-sec)]">
              Rated: <span className="font-black font-mono text-[var(--brand-text)]">{doc.rating}/10</span>
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {canClaim && (
            <button
              data-testid="claim-button"
              onClick={handleClaim}
              disabled={claiming}
              className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {claiming ? <Loader2 size={18} className="animate-spin" /> : null}
              {claiming ? 'Claiming...' : 'Claim This Task'}
            </button>
          )}

          {canWork && (
            <button
              data-testid="open-workspace-btn"
              onClick={() => navigate(`/workspace/${id}`)}
              className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2"
            >
              Open Workspace
            </button>
          )}

          {canApprove && (
            <button
              data-testid="approve-transcription-btn"
              onClick={handleApprove}
              disabled={approving}
              className="w-full bg-[var(--brand-accent)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-accent)]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {approving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              Approve Transcription
            </button>
          )}

          {canRevise && (
            <button
              data-testid="request-revision-btn"
              onClick={() => setRevisionOpen(true)}
              className="w-full bg-white text-[var(--brand-warning)] border border-[var(--brand-warning)] rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-warning)]/5 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw size={18} /> Request Revision
            </button>
          )}

          {canRate && (
            <button
              data-testid="rate-button"
              onClick={() => setRatingOpen(true)}
              className="w-full bg-[var(--brand-warning)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-warning)]/90 transition-colors flex items-center justify-center gap-2"
            >
              <Star size={18} /> Rate Transcriber
            </button>
          )}
        </div>

        {/* Rating Dialog */}
        <Dialog open={ratingOpen} onOpenChange={setRatingOpen}>
          <DialogContent className="rounded-sm" data-testid="rating-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-tight">Rate Transcription</DialogTitle>
              <DialogDescription className="text-sm text-[var(--brand-text-sec)]">Rate quality (1-10)</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-[var(--brand-text-sec)]">Rating</span>
                  <span className="text-2xl font-black font-mono text-[var(--brand-primary)]" data-testid="rating-value">{rating[0]}/10</span>
                </div>
                <Slider data-testid="rating-slider" value={rating} onValueChange={setRating} min={1} max={10} step={1} />
              </div>
              <textarea
                data-testid="rating-comment-input"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Leave a comment (optional)"
                rows={3}
                className="w-full rounded-sm border border-[var(--brand-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
              />
            </div>
            <DialogFooter>
              <button data-testid="submit-rating-button" onClick={handleRate} className="bg-[var(--brand-primary)] text-white rounded-sm px-6 py-2 font-medium hover:bg-[var(--brand-primary-hover)]">
                Submit Rating
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revision Dialog */}
        <Dialog open={revisionOpen} onOpenChange={setRevisionOpen}>
          <DialogContent className="rounded-sm" data-testid="revision-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-tight">Request Revision</DialogTitle>
              <DialogDescription className="text-sm text-[var(--brand-text-sec)]">Describe what needs to be changed</DialogDescription>
            </DialogHeader>
            <textarea
              data-testid="revision-notes-input"
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder="Please correct the third paragraph..."
              rows={4}
              className="w-full rounded-sm border border-[var(--brand-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            />
            <DialogFooter>
              <button data-testid="submit-revision-button" onClick={handleRevision} className="bg-[var(--brand-warning)] text-white rounded-sm px-6 py-2 font-medium hover:bg-[var(--brand-warning)]/90">
                Submit Revision Request
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
